import React, { useState, useEffect } from 'react';
import '../styles/pages/MamdaniPage.css';

const API = 'https://api.anti-bot.ai';

const BOT_TYPE_COLOR = {
  BOT:        '#FF3B3B',
  LIKELY_BOT: '#FF7A00',
  SUSPICIOUS: '#FFD600',
  REVIEW:     '#7ECEFF',
};

function BotRow({ bot }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr
        className={`mam-watchlist-row ${open ? 'mam-watchlist-row--open' : ''}`}
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer' }}
      >
        <td>
          <span className="mam-bot-badge" style={{ background: BOT_TYPE_COLOR[bot.bot_type] || '#888' }}>
            {bot.bot_type}
          </span>
        </td>
        <td className="mam-wl-user">
          <a
            href={`https://twitter.com/${bot.username}`}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
          >
            @{bot.username}
          </a>
        </td>
        <td><strong>{bot.bot_score}</strong>/100</td>
        <td>
          {bot.anti_israel_count > 0
            ? <span className="mam-anti-flag">⚠ {bot.anti_israel_count}/50</span>
            : <span className="mam-no-anti">—</span>}
        </td>
        <td>
          {bot.is_coordinated
            ? <span className="mam-coordinated">COORDINATED</span>
            : '—'}
        </td>
        <td className="mam-wl-expand">{open ? '▲' : '▼'}</td>
      </tr>

      {open && (
        <tr className="mam-watchlist-detail">
          <td colSpan={6}>
            <div className="mam-detail-box">

              <div className="mam-detail-section">
                <strong>Why bot:</strong>
                <ul>
                  {(bot.reasons || []).map((r, j) => <li key={j}>{r}</li>)}
                </ul>
              </div>

              {bot.repost_ratio > 0 && (
                <div className="mam-detail-section">
                  <strong>Repost ratio:</strong> {(bot.repost_ratio * 100).toFixed(0)}% of last posts are reposts
                </div>
              )}

              {bot.anti_israel_count > 0 && (
                <div className="mam-detail-section">
                  <strong>Anti-Israel examples ({bot.anti_israel_count}/50):</strong>
                  <ul>
                    {(bot.anti_israel_examples || []).map((ex, j) => (
                      <li key={j} className="mam-example">{ex}</li>
                    ))}
                  </ul>
                </div>
              )}

              {bot.is_coordinated && (
                <div className="mam-detail-coordinated">
                  Appeared on: {(bot.found_on_accounts || []).join(', ')} — likely a coordinated party bot
                </div>
              )}

            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function BotsTable({ bots }) {
  if (!bots.length) return <div className="mam-empty">No bots found for this account</div>;

  return (
    <div className="mam-watchlist">
      <table className="mam-watchlist-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>User</th>
            <th>Score</th>
            <th>Anti-Israel</th>
            <th>Coordinated</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {bots.map((b) => <BotRow key={b.username} bot={b} />)}
        </tbody>
      </table>
    </div>
  );
}

function fmt(n) {
  if (!n) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

function TweetDisplay({ username, display_name, profile_image_url, tweet_text, tweeted_at, tweet_id, like_count, retweet_count, reply_count }) {
  if (!tweet_text) {
    return <div className="mam-empty">No tweet saved — run the pipeline to populate</div>;
  }

  const date = tweeted_at
    ? new Date(tweeted_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  return (
    <div className="mam-tweet-box">
      <div className="mam-tweet-header">
        {profile_image_url && (
          <img className="mam-tweet-avatar" src={profile_image_url} alt={display_name} />
        )}
        <div className="mam-tweet-author">
          <span className="mam-tweet-name">{display_name}</span>
          <span className="mam-tweet-handle">@{username}</span>
        </div>
      </div>

      <div className="mam-tweet-text">{tweet_text}</div>

      <div className="mam-tweet-stats">
        <span className="mam-tweet-stat">
          <span className="mam-tweet-stat-icon">❤</span> {fmt(like_count)}
        </span>
        <span className="mam-tweet-stat">
          <span className="mam-tweet-stat-icon">🔁</span> {fmt(retweet_count)}
        </span>
        <span className="mam-tweet-stat">
          <span className="mam-tweet-stat-icon">💬</span> {fmt(reply_count)}
        </span>
      </div>

      {tweet_id && (
        <a
          className="mam-tweet-link"
          href={`https://twitter.com/${username}/status/${tweet_id}`}
          target="_blank"
          rel="noreferrer"
        >
          View on X ↗
        </a>
      )}
    </div>
  );
}

export default function MamdaniPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/mamdani/watchlist-data`)
      .then(r => r.json())
      .then(d => { if (d.success) setAccounts(d.accounts || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const current = accounts[selected] || null;

  // All bots across all accounts, deduped by username (keep highest score)
  const allBotMap = {};
  for (const acc of accounts) {
    for (const b of (acc.bots || [])) {
      if (!allBotMap[b.username] || b.bot_score > allBotMap[b.username].bot_score) {
        allBotMap[b.username] = b;
      }
    }
  }
  const allBots = Object.values(allBotMap).sort(
    (a, b) => b.is_coordinated - a.is_coordinated || b.bot_score - a.bot_score
  );

  const botsCount     = allBots.filter(b => b.bot_type === 'BOT' || b.bot_type === 'LIKELY_BOT').length;
  const antiCount     = allBots.filter(b => b.anti_israel_count > 0).length;
  const coordCount    = allBots.filter(b => b.is_coordinated).length;

  if (loading) {
    return (
      <div className="mam-page" dir="ltr">
        <div className="mam-loading"><div className="spinner" />Loading data...</div>
      </div>
    );
  }

  return (
    <div className="mam-page" dir="ltr">

      <div className="mam-header">
        <div className="mam-header-title">
          <span className="mam-header-dot" />
          <h1>Bot Analysis — Mamdani</h1>
        </div>
        <p className="mam-header-sub">Detecting anti-Israel bots amplifying pro-Mamdani accounts</p>
      </div>

      {/* Account tabs */}
      <div className="mam-account-tabs">
        {accounts.map((acc, i) => (
          <button
            key={acc.username}
            className={`mam-account-tab ${selected === i ? 'mam-account-tab--active' : ''}`}
            onClick={() => setSelected(i)}
          >
            <span className="mam-account-label">@{acc.username}</span>
            <span className="mam-account-sub">
              {acc.bots.length > 0 ? `${acc.bots.length} bots` : 'no data'}
            </span>
          </button>
        ))}
      </div>

      {/* Per-account: latest tweet + bots */}
      {current && (
        <div className="mam-grid-top">
          <div className="mam-card">
            <div className="mam-card-title">Latest Tweet</div>
            <TweetDisplay
              username={current.username}
              display_name={current.display_name}
              profile_image_url={current.profile_image_url}
              tweet_text={current.latest_tweet_text}
              tweeted_at={current.tweeted_at}
              tweet_id={current.latest_tweet_id}
              like_count={current.like_count}
              retweet_count={current.retweet_count}
              reply_count={current.reply_count}
            />
          </div>

          <div className="mam-card">
            <div className="mam-card-title">
              Bots that retweeted this tweet ({current.bots.length})
            </div>
            <BotsTable bots={current.bots} />
          </div>
        </div>
      )}

      {/* Global watchlist */}
      <div className="mam-card mam-card--full">
        <div className="mam-card-title-row">
          <div className="mam-card-title">All Bots — Watchlist</div>
          <div className="mam-watchlist-stats">
            <span className="mam-stat">
              <span className="mam-stat-dot mam-stat-dot--red" />
              {botsCount} Bots
            </span>
            <span className="mam-stat">
              <span className="mam-stat-dot mam-stat-dot--orange" />
              {antiCount} Anti-Israel
            </span>
            <span className="mam-stat">
              <span className="mam-stat-dot mam-stat-dot--purple" />
              {coordCount} Coordinated
            </span>
          </div>
        </div>
        {allBots.length === 0
          ? <div className="mam-empty">No bots found yet — run the pipeline first</div>
          : <BotsTable bots={allBots} />
        }
      </div>

    </div>
  );
}
